import React, { Fragment, useEffect, useRef, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import Select from "react-select";
import "./cleanServAppointment.css";

const isValidName = (name) => name.trim() !== "";
const isValidNotice = (notice) => notice.trim() !== "";
const isValidCity = (city) => city !== "";
const isValidDistrict = (district) => district.trim() !== "";
const isValidAcType = (acType) => acType !== null;
const isValidCount = (count) => /^[1-9]\d*$/.test(count);
const isValidSelectedFile = (selectedFile) => {
  if (!selectedFile) return true;
  const validImageTypes = ["image/jpeg", "image/png", "image/gif"];
  return validImageTypes.includes(selectedFile.type);
};

const CleanServAppointment = () => {
  const token = localStorage.getItem("token");
  const mobileNumber = localStorage.getItem("mobileNumber");
  const [selectedFile, setSelectedFile] = useState(null);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && isValidSelectedFile(file)) {
      setSelectedFile(file);
      setSelectedFileError("");
    } else if (file) {
      setSelectedFileError("برجاء ادخال صيغة صوره صحيحه (JPEG, PNG, GIF)");
    } else {
      setSelectedFile(null);
      setSelectedFileError("");
    }
  };

  const [isSending, setIsSending] = useState(false);
  const [name, setName] = useState("");
  const [region, setRegion] = useState("");
  const [regionId, setRegionId] = useState("");
  const [selectedCity, setSelectedCity] = useState(null);
  const [district, setDistrict] = useState("");
  const [acType, setAcType] = useState(null);
  const [notice, setNotice] = useState("");
  const [data, setData] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAcTypesOpen, setIsAcTypesOpen] = useState(false);
  const [regions, setRegions] = useState([]);
  const [acCount, setAcCount] = useState("");

  const [nameError, setNameError] = useState("");
  const [cityError, setCityError] = useState("");
  const [districtError, setDistrictError] = useState("");
  const [acTypesError, setAcTypesError] = useState("");
  const [selectedFileError, setSelectedFileError] = useState("");
  const [acCountError, setAcCountError] = useState("");
  const [noticeError, setNoticeError] = useState("");

  const myImageInputRef = useRef(null);
  const acTypesRef = useRef(null);
  const baseUrl = "https://api.generalhome.sa";

  const api = axios.create({
    baseURL: baseUrl,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  // Fetch regions from API
  const fetchRegions = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/MaintenanceCity/GetMaintenanceMainCities`
      );
      setRegions(response.data);
    } catch (error) {
      toast.error("❌ حدث خطأ أثناء تحميل المناطق");
      console.error("Error fetching regions:", error);
    }
  };

  // Fetch cities by region ID
  const fetchCitiesByRegion = async (mainCityId) => {
    try {
      const response = await api.get(
        `/api/MaintenanceCity/GetallCitiesByMainCityId?MainCityId=${mainCityId}`
      );
      setCities(response.data);
      setLoading(false);
    } catch (error) {
      toast.error("❌ حدث خطأ أثناء تحميل المدن");
      console.error("Error fetching cities:", error);
      setCities([]);
      setLoading(false);
    }
  };

  const uploadImage = async () => {
    if (!selectedFile) return { success: true, imageUrl: "" };

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const response = await api.post(
        `${baseUrl}/api/Themes/UploadImages`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const imageUrl = response.data?.imageUrl || response.data;
      if (!imageUrl) throw new Error("Failed to upload image");

      const fullImageUrl = imageUrl.startsWith("http")
        ? imageUrl
        : `${baseUrl}${imageUrl.startsWith("/") ? "" : "/"}${imageUrl}`;

      return { success: true, imageUrl: fullImageUrl };
    } catch (error) {
      console.error("Error uploading image:", error);
      if (error.response?.status === 401) {
        Swal.fire({
          icon: "error",
          text: "انتهت جلستك، برجاء تسجيل الدخول مرة أخرى",
        });
      }
      return { success: false, error: "فشل رفع الصورة" };
    }
  };

  const handleAcTypeSelect = (acTypeId) => {
    setAcType(acTypeId);
    setAcTypesError("");
    setIsAcTypesOpen(false);
  };

  const getAcTypeName = (id) => {
    const type = data.find((item) => item.ACTypeId === id);
    return type ? type.Name : "";
  };

  const validateForm = () => {
    const nameIsValid = isValidName(name);
    const cityIsValid = isValidCity(selectedCity?.value || "");
    const districtIsValid = isValidDistrict(district);
    const acTypeIsValid = isValidAcType(acType);
    const selectedFileIsValid = isValidSelectedFile(selectedFile);
    const acCountIsValid = isValidCount(acCount);
    const noticeIsValid = isValidNotice(notice);

    setNameError(nameIsValid ? "" : "برجاء ادخال الاسم (حقل مطلوب)");
    setCityError(cityIsValid ? "" : "برجاء اختيار المدينة (حقل مطلوب)");
    setDistrictError(districtIsValid ? "" : "برجاء ادخال اسم الحي (حقل مطلوب)");
    setAcTypesError(acTypeIsValid ? "" : "برجاء اختيار نوع المكيف (حقل مطلوب)");
    setSelectedFileError(
      selectedFileIsValid ? "" : "برجاء ادخال صيغة صوره صحيحه (JPEG, PNG, GIF)"
    );
    setAcCountError(
      acCountIsValid ? "" : "برجاء ادخال عدد المكيفات صحيح (حقل مطلوب)"
    );
    setNoticeError(noticeIsValid ? "" : "(حقل مطلوب) برجاء ادخال الملاحظات");

    return (
      noticeIsValid &&
      nameIsValid &&
      cityIsValid &&
      districtIsValid &&
      acTypeIsValid &&
      acCountIsValid &&
      selectedFileIsValid
    );
  };

  const submit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      // Scroll to the first error
      const firstErrorElement = document.querySelector(".text-danger");
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
      return;
    }

    setIsSending(true);
    try {
      const uploadResult = await uploadImage();
      if (!uploadResult.success) {
        Swal.fire({
          icon: "error",
          text: uploadResult.error || "فشل رفع الصورة، برجاء المحاولة مرة أخرى",
        });
        return;
      }

      const payload = {
        MaintenanceTypeId: 2,
        CustomerName: name,
        PhoneNumber: mobileNumber,
        Region: region,
        Neighborhood: district,
        CityId: parseInt(selectedCity.value),
        ACTypeCount: parseInt(acCount),
        ErrorType: notice,
        ImagePath: uploadResult.imageUrl,
        Details: [
          {
            ACTypeId: parseInt(acType),
            ACTypeCount: 1,
            WarrantyStatusId: 2,
          },
        ],
      };

      const response = await api.post(`/api/maintenance/request`, payload);

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "تم بنجاح",
          text:
            response.data.message ||
            "تم ارسال طلبك بنجاح، سيتم التواصل معك قريباً",
          confirmButtonText: "حسناً",
        });
        // Reset form
        setName("");
        setRegion("");
        setRegionId("");
        setSelectedCity(null);
        setDistrict("");
        setAcType(null);
        setNotice("");
        setSelectedFile(null);
        setAcCount("");
        if (myImageInputRef.current) myImageInputRef.current.value = "";
      } else {
        Swal.fire({
          icon: "error",
          title: "خطأ",
          text: response.data.message || "حدث خطأ، برجاء المحاولة مرة أخرى",
          confirmButtonText: "حسناً",
        });
      }
    } catch (error) {
      console.error("Error:", error);
      if (error.response?.status === 401) {
        Swal.fire({
          icon: "error",
          title: "انتهت الجلسة",
          text: "انتهت جلستك، برجاء تسجيل الدخول مرة أخرى",
          confirmButtonText: "حسناً",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "خطأ في الاتصال",
          text: "حدث خطأ في الاتصال بالخادم. برجاء المحاولة لاحقاً.",
          confirmButtonText: "حسناً",
        });
      }
    } finally {
      setIsSending(false);
    }
  };

  const getAllAcTypes = async () => {
    try {
      const response = await api.get(`/api/ACTypes`);
      setData(response.data);
    } catch (err) {
      console.log(err.message);
      if (err.response?.status === 401) {
        Swal.fire({
          icon: "error",
          title: "انتهت الجلسة",
          text: "انتهت جلستك، برجاء تسجيل الدخول مرة أخرى",
          confirmButtonText: "حسناً",
        });
      }
    }
  };

  const buttonRef = useRef(null);

  useEffect(() => {
    getAllAcTypes();
    fetchRegions();

    const handleClickOutside = (event) => {
      if (acTypesRef.current && !acTypesRef.current.contains(event.target)) {
        setIsAcTypesOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch cities when region changes
  useEffect(() => {
    if (regionId) {
      fetchCitiesByRegion(regionId);
      setSelectedCity(null);
    } else {
      setCities([]);
      setSelectedCity(null);
    }
  }, [regionId]);

  const handleRegionChange = (e) => {
    const selectedValue = e.target.value;
    if (selectedValue === "") {
      setRegion("");
      setRegionId("");
      return;
    }

    const selectedRegion = regions.find(
      (r) => r.NameMainCity === selectedValue
    );
    if (selectedRegion) {
      setRegion(selectedRegion.NameMainCity);
      setRegionId(selectedRegion.Id);
    }
  };

  const cityOptions = cities.map((city) => ({
    value: city.Id,
    label: city.Name,
  }));

  const handleCityChange = (selectedOption) => {
    setSelectedCity(selectedOption);
    setCityError("");
  };

  // Custom styles for react-select
  const customStyles = {
    control: (provided) => ({
      ...provided,
      padding: "0.375rem 0.75rem",
      border: "1px solid #ced4da",
      borderRadius: "0.25rem",
      minHeight: "38px",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  };

  return (
    <section className="cleanServAppointment">
      <Helmet>
        <meta charSet="utf-8" />
        <title>حجز طلب التنظيف</title>
        <link
          rel="canonical"
          href="https://generalhome.sa/cleaning-appointment"
        />
      </Helmet>
      <div className="cleanServAppointment-container container">
        <form onSubmit={submit}>
          <div className="inputs-box inputs-box1">
            <div className="div">
              <label htmlFor="name">
                الاسم <span className="text-danger">*</span>
              </label>
              <input
                placeholder="الاسم"
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setNameError("");
                }}
              />
              {nameError && <p className="text-danger">{nameError}</p>}
            </div>
            <div className="div phoneNum">
              <label htmlFor="number">رقم الهاتف</label>
              <input
                placeholder="رقم الهاتف (باللغه الانجليزيه)"
                type="text"
                value={mobileNumber}
                readOnly={true}
              />
            </div>
          </div>
          <div className="inputs-box">
            <div className="div">
              <label htmlFor="region">
                المنطقة <span className="text-danger">*</span>
              </label>
              <select id="region" value={region} onChange={handleRegionChange}>
                <option value="">اختر المنطقة</option>
                {regions.map((region) => (
                  <option key={region.Id} value={region.NameMainCity}>
                    {region.NameMainCity}
                  </option>
                ))}
              </select>
            </div>

            <div className="div">
              <label htmlFor="city">
                المدينة <span className="text-danger">*</span>
              </label>
              <Select
                id="city"
                value={selectedCity}
                onChange={handleCityChange}
                options={cityOptions}
                placeholder="اختر المدينة"
                isSearchable={true}
                noOptionsMessage={() => "لا توجد نتائج"}
                className="basic-single"
                classNamePrefix="select"
                isRtl={true}
                isLoading={loading}
                styles={customStyles}
                aria-label="اختر المدينة"
                isDisabled={!regionId}
              />
              {cityError && <p className="text-danger">{cityError}</p>}
            </div>

            <div className="div">
              <label htmlFor="district">
                الحي <span className="text-danger">*</span>
              </label>
              <input
                className="w-100"
                placeholder="الحي"
                type="text"
                value={district}
                onChange={(e) => {
                  setDistrict(e.target.value);
                  setDistrictError("");
                }}
              />
              {districtError && <p className="text-danger">{districtError}</p>}
            </div>
          </div>
          <div className="selectionList">
            <div className="selections-box">
              <div className="selection-box mb-3 w-100" ref={acTypesRef}>
                <label htmlFor="acTypes">
                  نوع المكيف <span className="text-danger">*</span>
                </label>
                <div
                  className="ac-types-selector"
                  onClick={() => setIsAcTypesOpen(!isAcTypesOpen)}
                >
                  <div className="ac-types-placeholder">
                    {acType ? getAcTypeName(acType) : "اختر نوع المكيف"}
                  </div>
                  <div
                    className={`ac-types-dropdown ${
                      isAcTypesOpen ? "open" : ""
                    }`}
                  >
                    {data.map((item) => (
                      <div
                        key={item.ACTypeId}
                        className={`ac-types-option ${
                          acType === item.ACTypeId ? "selected" : ""
                        }`}
                        onClick={() => handleAcTypeSelect(item.ACTypeId)}
                      >
                        {item.Name}
                      </div>
                    ))}
                  </div>
                </div>
                {acTypesError && <p className="text-danger">{acTypesError}</p>}
              </div>
            </div>
          </div>
          <div className="div">
            <label htmlFor="acCount">
              عدد المكيفات <span className="text-danger">*</span>
            </label>
            <input
              className="w-100"
              placeholder="عدد المكيفات"
              type="number"
              min="1"
              value={acCount}
              onChange={(e) => {
                setAcCount(e.target.value);
                setAcCountError("");
              }}
            />
            {acCountError && <p className="text-danger">{acCountError}</p>}
          </div>
          <div>
            <label className="d-block mb-2">برجاء رفع صوره الفاتوره</label>
            <input
              type="file"
              name="image"
              className="form-control-file border-0"
              id="myImageInput"
              onChange={handleFileChange}
              ref={myImageInputRef}
              accept="image/jpeg, image/png, image/gif"
            />
            {selectedFileError && (
              <p className="text-danger">{selectedFileError}</p>
            )}
          </div>

          <div className="textarea-box">
            <label htmlFor="textarea">وصف المشكله</label>
            <textarea
              name="textarea"
              placeholder="إكتب وصف المشكله...."
              value={notice}
              onChange={(e) => setNotice(e.target.value)}
            ></textarea>
            {noticeError && <p className="text-danger">{noticeError}</p>}
          </div>
          <div className="modal-actions">
            <button
              ref={buttonRef}
              id="myButton"
              type="submit"
              disabled={isSending}
              className={isSending ? "disabled-style" : ""}
            >
              {isSending ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  جاري الارسال...
                </>
              ) : (
                "ارسال الطلب"
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default CleanServAppointment;
